import { BOOLEAN_SELECT_LIST_ITEMS, Checkbox, Grid, pn, Stack, useElementBreakpoints, Typography } from "@compass/core";
import { DatePicker, DurationInput, Input, Select } from "components";
import { LkStudentServiceDisplayFieldId, StudentServiceFilter as StudentServiceFilterModel, StudentServiceFilterResource } from "generated/openApi";
import produce from "immer";
import { useMemo } from "react";
import ApiService from "services/apiService";
import { InfoIcon } from "components/common/InfoIcon";
import { FilterAccordion } from "components/common/CustomFilters/FilterAccordion";

type InputType =
  | "customFilters"
  | "date"
  | "dateFrom"
  | "dateTo"
  | "student"
  | "serviceType"
  | "serviceSubType"
  | "serviceRecipient"
  | "provider"
  | "flagForReview"
  | "duration"
  | "hasStudentSignatures"
  | "hasStaffSignatures"
  | "hasAttachments"
// | "isLocked"
// | "serviceName"
// | "parentCount";

export const StudentServiceFilterAccordion = (props: {
  resource?: StudentServiceFilterResource;
  filter: StudentServiceFilterModel;
  setFilter: (filter: StudentServiceFilterModel) => void;
  showInputs?: InputType[];
}) => {
  const { breakpointsRef, xSmallFieldBreakpoints,smallFieldBreakpoints, largeFieldBreakpoints } = useElementBreakpoints();
  const properties = props.resource?.properties || [];

  const displayFields = props.resource?.displayFields || [];
  const flagForReviewDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.FlagForReview);

  const serviceSubTypes =
    props.filter.serviceTypeIds && props.filter.serviceTypeIds.length === 1
      ? props.resource?.serviceSubTypes?.filter((x) => x.serviceTypeId === props.filter.serviceTypeIds![0])
      : undefined;


  let inputs: InputType[] = [
    "customFilters",
    "dateFrom",
    "dateTo",
    "student",
    "serviceType",
    "serviceSubType",
    "serviceRecipient",
    "provider",
    "duration",
    "flagForReview",
    "hasStudentSignatures",
    "hasStaffSignatures",
    "hasAttachments",
    // "isLocked",
    // "serviceName",
    // "parentCount",
  ];

  if (props.showInputs) inputs = props.showInputs;

  //creating the date only once
  const yesterdayDate = useMemo(() => {
    let yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  }, []);

  const dateField = () => {
    var propertyName = pn<StudentServiceFilterModel>("date");
    var property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.date}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.date = value;
            })
          )
        }
      />
    );
  };

  const dateFromField = () => {
    const propertyName = pn<StudentServiceFilterModel>("dateFrom");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.dateFrom}
        maxDate={yesterdayDate}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateFrom = value;
            })
          )
        }
      />
    );
  };

  const dateToField = () => {
    const propertyName = pn<StudentServiceFilterModel>("dateTo");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.dateTo}
        maxDate={yesterdayDate}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateTo = value;
            })
          )
        }
      />
    );
  };

  const studentField = () => {
    const propertyName = pn<StudentServiceFilterModel>("student");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={props.filter.student}
        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
        onChange={(e, value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.student = value;
              draft.studentId = value?.id;
            })
          );
        }}
        fetchFunction={(searchString: string) => ApiService.search.students(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const serviceTypeField = () => {
    const propertyName = pn<StudentServiceFilterModel>("serviceTypeIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource?.serviceTypes?.filter((x) => props.filter.serviceTypeIds?.includes(x.id))}
        options={props.resource?.serviceTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.serviceTypeIds = values.map((x) => x.id);
              draft.serviceSubTypeIds = [];
            })
          )
        }
      />
    );
  };

  const serviceSubTypeField = () => {
    const propertyName = pn<StudentServiceFilterModel>("serviceSubTypeIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource?.serviceSubTypes?.filter((x) => props.filter.serviceSubTypeIds?.includes(x.id))}
        options={serviceSubTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.serviceSubTypeIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const serviceRecipientField = () => {
    const propertyName = pn<StudentServiceFilterModel>("serviceRecipientId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={props.resource?.serviceRecipients?.find((x) => x.id === props.filter.serviceRecipientId)}
        options={props.resource?.serviceRecipients}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.serviceRecipientId = value?.id;
            })
          )
        }
      />
    );
  };

  const flagForReviewField = () => {
    const propertyName = pn<StudentServiceFilterModel>("flagForReview");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Checkbox
        label={property?.displayName}
        checked={props.filter.flagForReview}
        onClick={(e) => props.setFilter(produce(props.filter, (draft) => {
          draft.flagForReview = !props.filter.flagForReview;
        }))}
      />
    );
  };

  const hasStudentSignatures = () => {
    const propertyName = pn<StudentServiceFilterModel>("hasStudentSignatures");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Checkbox
        label={property?.displayName}
        checked={props.filter.hasStudentSignatures}
        onClick={(e) => props.setFilter(produce(props.filter, (draft) => {
          draft.hasStudentSignatures = !props.filter.hasStudentSignatures;
        }))}
      />
    );
  };

  const hasStaffSignatures = () => {
    const propertyName = pn<StudentServiceFilterModel>("hasStaffSignatures");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Checkbox
        label={property?.displayName}
        checked={props.filter.hasStaffSignatures}
        onClick={(e) => props.setFilter(produce(props.filter, (draft) => {
          draft.hasStaffSignatures = !props.filter.hasStaffSignatures;
        }))}
      />
    );
  };

  const hasAttachments = () => {
    const propertyName = pn<StudentServiceFilterModel>("hasAttachments");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Checkbox
        label={property?.displayName}
        checked={props.filter.hasAttachments}
        onClick={(e) => props.setFilter(produce(props.filter, (draft) => {
          draft.hasAttachments = !props.filter.hasAttachments;
        }))}
      />
    );
  };

  const providerField = () => {
    const propertyName = pn<StudentServiceFilterModel>("provider");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={props.filter.provider}
        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
        onChange={(e, value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.provider = value;
              draft.providerId = value?.id;
            })
          );
        }}
        fetchFunction={(searchString: string) => ApiService.search.providers(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const durationField = () => {
    const propertyName = pn<StudentServiceFilterModel>("durationInMinutes");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DurationInput
        label={property?.displayName}
        value={props.filter.durationInMinutes}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.durationInMinutes = value!;
            })
          )
        }
      />
    );
  };

  const isLockedField = () => {
    const propertyName = pn<StudentServiceFilterModel>("isLocked");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.filter.isLocked)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.isLocked = value?.id;
            })
          )
        }
      />
    );
  };

  const serviceNameField = () => {
    const propertyName = pn<StudentServiceFilterModel>("serviceName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        value={props.filter.serviceName}
        onChange={(e) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.serviceName = e.target.value;
            })
          )
        }
      />
    );
  };

  const parentCountField = () => {
    const propertyName = pn<StudentServiceFilterModel>("parentCount");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Input
          modelProperty={property}
          value={props.filter.parentCount}
          onChange={(e) =>
            props.setFilter(
              produce(props.filter, (draft) => {
                draft.parentCount = parseFloat(e.target.value);
              })
            )
          }
        />
        <InfoIcon text="Number of Parents Per Student" />
      </Stack>
    );
  };

  return (
    <Grid>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        Dates must be yesterday or earlier
      </Typography>
      <Grid ref={breakpointsRef} container spacing="sm">
        {inputs.includes("date") && (
          <Grid item xs={smallFieldBreakpoints}>
            {dateField()}
          </Grid>
        )}

        {inputs.includes("dateFrom") && (
          <Grid item xs={smallFieldBreakpoints}>
            {dateFromField()}
          </Grid>
        )}

        {inputs.includes("dateTo") && (
          <Grid item xs={smallFieldBreakpoints}>
            {dateToField()}
          </Grid>
        )}

        {inputs.includes("student") && (
          <Grid item xs={smallFieldBreakpoints}>
            {studentField()}
          </Grid>
        )}

        {inputs.includes("serviceType") && (
          <Grid item xs={largeFieldBreakpoints}>
            {serviceTypeField()}
          </Grid>
        )}

        {inputs.includes("serviceSubType") && serviceSubTypes && serviceSubTypes.length > 0 && (
          <Grid item xs={largeFieldBreakpoints}>
            {serviceSubTypeField()}
          </Grid>
        )}

        {inputs.includes("serviceRecipient") && (
          <Grid item xs={smallFieldBreakpoints}>
            {serviceRecipientField()}
          </Grid>
        )}

        {inputs.includes("provider") && (
          <Grid item xs={smallFieldBreakpoints}>
            {providerField()}
          </Grid>
        )}

        {inputs.includes("duration") && (
          <Grid item xs={smallFieldBreakpoints}>
            {durationField()}
          </Grid>
        )}

        {inputs.includes("flagForReview") && (
          <Grid item xs={smallFieldBreakpoints}>
            {flagForReviewField()}
          </Grid>
        )}

        {inputs.includes("hasStudentSignatures") && (
          <Grid item xs={xSmallFieldBreakpoints}>
            {hasStudentSignatures()}
          </Grid>
        )}

        {inputs.includes("hasStaffSignatures") && (
          <Grid item xs={xSmallFieldBreakpoints}>
            {hasStaffSignatures()}
          </Grid>
        )}

        {inputs.includes("hasAttachments") && (
          <Grid item xs={xSmallFieldBreakpoints}>
            {hasAttachments()}
          </Grid>
        )}

        {/* {inputs.includes("isLocked") && (
            <Grid item xs={smallFieldBreakpoints}>
              {isLockedInput}
            </Grid>
          )}

          {inputs.includes("serviceName") && (
            <Grid item xs={smallFieldBreakpoints}>
              {serviceNameInput}
            </Grid>
          )}

          {inputs.includes("parentCount") && (
            <Grid item xs={smallFieldBreakpoints}>
              {parentCountInput}
            </Grid>
          )} */}
      </Grid>
    </Grid>
  );
};
